import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useMusicPlayer } from '../MusicPlayerContext';
import { spaNavigate } from '../utils/spaNavigate';
import { shareMusic, copyToClipboard } from '../utils/shareMusic';
import { downloadMusic } from '../utils/downloadMusic';
import { getCachedMusicMetadata, isFileCached } from '../utils/musicCache';
import { showUiHint } from '../utils/uiHint';
import DownloadDialog from './DownloadDialog';
import MusicLicenseModal from './MusicLicenseModal';
import SEO from './SEO';
import { getCoverImageUrl, getLyricsStreamUrl, hasVideo } from '../utils/musicStreamUrls';
import { isInstrumentalMusic, normalizeInstrumentalTitle } from '../utils/musicMeta';
import { looksLikeHtmlDocument, stripTimeTagsToPlainText } from '../utils/lyricsContent';
import { translateTag } from '../utils/tagTranslations';
import { useUser } from '../UserContext';
import { AdminContext } from '../AdminContext';
import { getFreeMemberNewSongInfo } from '../utils/playbackGate';
import { theme } from '../ui/theme';
import ShareDialog from './ShareDialog';
import { getShareMeta, mergeShareMeta } from '../utils/getShareMeta';

// 避免在源码中直接出现旧地区码字面量（同时规避 eslint no-useless-concat）
const ZH_CN = 'zh\u002dCN';
const ZH_TW = 'zh\u002dTW';
const ZH_CN_LC = 'zh\u002dcn';
const ZH_TW_LC = 'zh\u002dtw';
const ZH_HK_LC = 'zh\u002dhk';
const ZH_MO_LC = 'zh\u002dmo';
const ZH_HANS = 'zh\u002dhans';
const ZH_HANT = 'zh\u002dhant';

const MusicDetail = ({ musicId }) => {
  // 从URL或props获取音乐ID
  const getMusicIdFromUrl = () => {
    const path = window.location.pathname;
    const match = path.match(/\/music\/(\d+)/);
    return match ? parseInt(match[1]) : null;
  };
  
  const id = musicId || getMusicIdFromUrl();
  const { i18n, t } = useTranslation();
  const currentLang = i18n?.language || 'cn';
  const { playMusicNow } = useMusicPlayer();
  const { user } = useUser();
  const { isAdmin } = useContext(AdminContext);
  const [music, setMusic] = useState(null);
  const [relatedMusics, setRelatedMusics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [showFullLyrics, setShowFullLyrics] = useState(false);
  const [lyrics, setLyrics] = useState(null);
  const [lyricsSearchQuery, setLyricsSearchQuery] = useState('');
  const [highlightedLyrics, setHighlightedLyrics] = useState(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [descriptionHtmlPath, setDescriptionHtmlPath] = useState(null);
  const [isCached, setIsCached] = useState(false); // 当前歌曲是否已缓存完成（音频可离线播放）
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogMeta, setShareDialogMeta] = useState(null);

  // 避免 useCallback 因为依赖 music 变化导致“请求循环”
  const musicRef = useRef(null);
  useEffect(() => {
    musicRef.current = music;
  }, [music]);

  // 详情页请求：用 fetch 绕开全局 axios 拦截器（避免因登录/刷新逻辑触发整页跳转，从而导致 XHR 被 abort）
  const fetchJsonWithTimeout = useCallback(async (url, options = {}, timeoutMs = 12000) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        ...options,
        signal: controller.signal
      });
      const text = await res.text().catch(() => '');
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch (_) { data = null; }
      return { ok: res.ok, status: res.status, data, rawText: text };
    } finally {
      clearTimeout(timeout);
    }
  }, []);

  // 统一按钮尺寸（对齐音乐库“中文歌曲”等筛选按钮：小尺寸胶囊按钮）
  const pillBtnBase =
    'px-2 py-0.5 text-xs rounded-full transition-colors inline-flex items-center gap-1 ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/25 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed';
  const pillBtnPrimary = theme.buttons.variant.primary;
  const pillBtnNeutral = theme.buttons.variant.outline;
  const pillBtnDanger = theme.buttons.variant.danger;

  const escapeHtml = (s) => {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const stripLrcToText = (lrc) => {
    const lines = String(lrc ?? '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    const out = [];
    for (const line of lines) {
      const s = String(line ?? '');
      // 匹配一个或多个时间标签，保留标签后的文本部分
      const m = s.match(/^\s*(\[[0-9]{1,2}:[0-9]{2}(?:\.[0-9]{1,3})?\])+([\s\S]*)$/);
      if (!m) continue;
      out.push(String(m[2] ?? '').replace(/\s+$/g, ''));
    }
    return out.join('\n');
  };

  const stripSrtToText = (srt) => {
    const lines = String(srt ?? '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    const out = [];
    let inText = false;
    for (const line of lines) {
      const s = String(line ?? '');
      const t = s.trim();
      if (!t) {
        inText = false;
        out.push('');
        continue;
      }
      if (/^\d+$/.test(t)) { inText = false; continue; }
      if (/^\d{2}:\d{2}:\d{2}[,.]\d{3}\s*-->\s*\d{2}:\d{2}:\d{2}[,.]\d{3}/.test(t)) {
        inText = true;
        continue;
      }
      if (inText) out.push(s);
    }
    return out.join('\n');
  };

  // 获取用户ID
  const getUserId = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'user_id') return value;
    }
    const userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    document.cookie = `user_id=${userId}; path=/; max-age=31536000`;
    return userId;
  };

  const getDirAndBaseFromFilePath = useCallback((music) => {
    const raw = music?.file_path;
    if (!raw) return null;
    const normalized = String(raw).replace(/\\/g, '/');
    const parts = normalized.split('/');
    const file = parts.pop() || '';
    const base = file.replace(/\.[^/.]+$/, '');
    const dir = parts.join('/');
    if (!dir || !base) return null;
    return { dir, base };
  }, []);

  const buildDescriptionHtmlPath = useCallback((music, langSuffix = '') => {
    const info = getDirAndBaseFromFilePath(music);
    if (!info) return null;
    const suffix = langSuffix ? `_${langSuffix}` : '';
    return `${info.dir}/${info.base}${suffix}.html`;
  }, [getDirAndBaseFromFilePath]);

  // 根据当前界面语言选择“歌曲说明 html”（翻译版优先，回退源 html）
  useEffect(() => {
    if (!music) {
      setDescriptionHtmlPath(null);
      return;
    }

    const normalizeLang = (lang) => {
      const s = String(lang || '').trim();
      if (!s) return 'cn';
      if (s === 'cn' || s === 'tw' || s === 'ja' || s === 'en') return s;
      const lower = s.toLowerCase().replace('_', '-');
      if (lower === 'zh' || lower === ZH_CN_LC || lower.startsWith(ZH_HANS)) return 'cn';
      if (lower === ZH_TW_LC || lower === ZH_HK_LC || lower === ZH_MO_LC || lower.startsWith(ZH_HANT)) return 'tw';
      return s;
    };

    const lang = normalizeLang(currentLang);
    const meta = music.metadata || null;
    const translations = meta?.description_html_translations || null;
    const translationList = Array.isArray(translations)
      ? translations
      : (translations && typeof translations === 'object' ? Object.keys(translations) : []);
    const langKey = (lang === 'cn') ? ZH_CN : (lang === 'tw' ? ZH_TW : lang);
    const translated = translationList.includes(langKey)
      ? buildDescriptionHtmlPath(music, langKey)
      : (translations && typeof translations === 'object'
        ? (translations[lang] || (lang === 'cn' ? translations[ZH_CN] : null) || (lang === 'tw' ? translations[ZH_TW] : null))
        : null);
    const fallback = (typeof meta?.description_html === 'string')
      ? meta.description_html
      : (meta?.description_html ? buildDescriptionHtmlPath(music) : null);
    setDescriptionHtmlPath(translated || fallback || null);
  }, [music, currentLang, buildDescriptionHtmlPath]);

  // 加载歌词（详情页：优先显示 txt 内容）
  const loadLyrics = useCallback(async (lyricsPath, lyricsTypeHint) => {
    try {
      const response = await fetch(lyricsPath, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Failed to load lyrics: ${response.status}`);
      const text = await response.text();
      const ct = response.headers.get('content-type') || '';
      if (looksLikeHtmlDocument(text, ct)) throw new Error('Lyrics response looks like HTML (SPA fallback)');

      const ext = (String(lyricsPath).split('.').pop() || '').toLowerCase();
      const type = String(lyricsTypeHint || ext || '').toLowerCase();
      let plain = text;
      if (type === 'lrc') plain = stripLrcToText(text);
      if (type === 'srt') plain = stripSrtToText(text);
      // 统一兜底：把可能残留的时间标签再剥一次
      plain = stripTimeTagsToPlainText(plain);

      const safe = escapeHtml(plain);
      setLyrics(safe);
      setHighlightedLyrics(safe);
    } catch (error) {
      console.error('Load lyrics error:', error);
    }
  }, []);

  const loadLyricsPreferTxt = useCallback(async (lyricsPath, lyricsTypeHint, musicObj = null) => {
    try {
      // 强制统一：优先走同一套歌词 URL 生成逻辑（支持开关：stream/直链）
      // - 统一走 stream（后端按同目录自动查找）
      // - 开关开启时：使用 stream（统一接口）
      const m = musicObj || musicRef.current;
      const directOrStream = (m && m.id) ? (getLyricsStreamUrl(m) || lyricsPath) : lyricsPath;
      const p = String(directOrStream || '');
      const isTxt = p.toLowerCase().endsWith('.txt');
      if (isTxt) return await loadLyrics(p, 'txt');

      const txtPath = p.replace(/\.(lrc|srt)$/i, '.txt');
      if (txtPath !== p) {
        const resp = await fetch(txtPath, { method: 'GET', cache: 'no-store' });
        if (resp.ok) {
          const text = await resp.text();
          const ct = resp.headers.get('content-type') || '';
          if (looksLikeHtmlDocument(text, ct)) throw new Error('Lyrics txt response looks like HTML (SPA fallback)');
          const safe = escapeHtml(text);
          setLyrics(safe);
          setHighlightedLyrics(safe);
          return;
        }
      }
    } catch (_) {}
    // 回退：加载原文件，并在前端解析成纯文本
    return await loadLyrics(lyricsPath, lyricsTypeHint);
  }, [loadLyrics]);

  // 搜索歌词
  const searchLyrics = (query) => {
    setLyricsSearchQuery(query);
    if (!query.trim()) {
      setHighlightedLyrics(lyrics);
      return;
    }

    if (!lyrics) return;

    // 高亮搜索关键词
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const highlighted = String(lyrics).replace(regex, '<mark class="bg-yellow-300">$1</mark>');
    setHighlightedLyrics(highlighted);
  };

  // 获取相关推荐
  const fetchRelatedMusics = useCallback(async (currentMusic) => {
    try {
      const sp = new URLSearchParams({
        lang: String(currentMusic.language_category || ''),
        page: '1',
        pageSize: '6',
        sort: 'popular'
      });
      const { ok, data } = await fetchJsonWithTimeout(`/api/music/list?${sp.toString()}`, {}, 12000);

      if (ok && data?.success) {
        // 排除当前音乐
        const musics = Array.isArray(data?.data?.musics) ? data.data.musics : [];
        const related = musics.filter(m => m.id !== currentMusic.id);
        setRelatedMusics(related.slice(0, 5));
      }
    } catch (error) {
      console.error('Fetch related musics error:', error);
    }
  }, [fetchJsonWithTimeout]);

  // 获取音乐详情
  const fetchMusicDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { ok, data } = await fetchJsonWithTimeout(`/api/music/${id}`, {}, 12000);

      if (ok && data?.success) {
        const musicData = data.data;
        setMusic(musicData);
        
        // 加载歌词
        if (musicData.lyrics_type) {
          loadLyricsPreferTxt(null, musicData.lyrics_type, musicData);
        }
        
        // 获取相关推荐
        fetchRelatedMusics(musicData);
      } else {
        setError((data && (data.error || data.message)) || t('musicDetail.fetchFailed'));
      }
    } catch (err) {
      console.error('Fetch music detail error:', err);
      const isAbort = err?.name === 'AbortError';
      setError(isAbort ? (t('hints.networkTimeout', '请求超时，请稍后重试')) : t('musicDetail.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, loadLyricsPreferTxt, fetchRelatedMusics, fetchJsonWithTimeout, t]);

  // 检查收藏状态
  const checkFavoriteStatus = useCallback(async () => {
    try {
      const userId = getUserId();
      const { ok, data } = await fetchJsonWithTimeout(`/api/music/${id}/favorite`, {
        headers: { 'X-User-ID': userId }
      }, 12000);

      if (ok && data?.success) {
        setIsFavorite(!!data?.data?.isFavorite);
        setFavoritesCount(Number(data?.data?.favoritesCount || 0));
      }
    } catch (error) {
      console.error('Check favorite error:', error);
    }
  }, [id, fetchJsonWithTimeout]);

  // 检查缓存状态（详情页：判断 IndexedDB 元数据 + 音频文件是否在 CacheStorage）
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!music || !music.id) {
          if (!cancelled) setIsCached(false);
          return;
        }
        const cachedMeta = await getCachedMusicMetadata(music.id);
        if (!cachedMeta) {
          if (!cancelled) setIsCached(false);
          return;
        }
        const audioPath = music.file_path;
        if (!audioPath) {
          if (!cancelled) setIsCached(false);
          return;
        }
        const ok = await isFileCached(audioPath);
        if (!cancelled) setIsCached(!!ok);
      } catch (_) {
        if (!cancelled) setIsCached(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [music]);

  useEffect(() => {
    if (id) {
      fetchMusicDetail();
      checkFavoriteStatus();
    }
  }, [id, fetchMusicDetail, checkFavoriteStatus]);

  // 切换收藏
  const toggleFavorite = async () => {
    try {
      const userId = getUserId();
      const response = isFavorite
        ? await axios.delete(`/api/music/${id}/favorite`, {
            headers: { 'X-User-ID': userId }
          })
        : await axios.post(`/api/music/${id}/favorite`, {}, {
            headers: { 'X-User-ID': userId }
          });

      if (response.data.success) {
        setIsFavorite(!isFavorite);
        setFavoritesCount(prev => isFavorite ? prev - 1 : prev + 1);
      }
    } catch (error) {
      const status = error?.response?.status;
      // 兼容：收藏记录不存在时，直接视为“已取消收藏”
      if (isFavorite && status === 404) {
        setIsFavorite(false);
        setFavoritesCount(prev => Math.max(0, prev - 1));
        return;
      }
      console.error('Toggle favorite error:', error);
      alert(t('musicList.operationFailed'));
    }
  };

  // 播放音乐
  const handlePlay = useCallback((e) => {
    if (music) {
      const gate = getFreeMemberNewSongInfo({ music, user, isAdmin: !!isAdmin });
      if (gate?.blocked) {
        const anchorEl = e?.currentTarget || null;
        showUiHint(t('musicList.freeMemberNewSongHint', { days: Number(gate?.daysLeft || 0) }), { anchorEl, duration: 2200 });
        return;
      }
      playMusicNow(music);
    }
  }, [music, user, isAdmin, playMusicNow, t]);

  // 下载音乐（打开下载对话框）
  const handleDownload = () => {
    if (!music) return;
    
    if (!music.allow_download) {
      alert(t('musicList.downloadNotAllowed'));
      return;
    }
    
    setDownloadDialogOpen(true);
  };

  // 确认下载（从对话框）
  const handleDownloadConfirm = async (formats, includeLyrics) => {
    if (!music) return;
    
    // 使用统一的下载函数
    await downloadMusic(music.id, music.allow_download, null, formats, includeLyrics);
  };

  // 分享音乐
  const handleShare = async () => {
    if (!music) return;
    
    const currentLang = i18n?.language || 'cn';
    
    // 使用统一的分享函数，分享成功后更新分享计数
    const shareResult = await shareMusic(music.id, currentLang);
    if (!shareResult) return;
    if (shareResult.usedWebShare || shareResult.isMobile) return;
    const pageMeta = getShareMeta();
    setShareDialogMeta(mergeShareMeta(pageMeta, shareResult?.shareInfo));
    setShareDialogOpen(true);
  };

  const handleShareCopy = (url) => {
    if (!url) return;
    copyToClipboard(url, () => {
      // 同时更新 music 对象中的 share_count
      setMusic(prev => prev ? { ...prev, share_count: (prev.share_count || 0) + 1 } : null);
    });
  };

  // 格式化时长
  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const locale = (String(currentLang || '') === 'tw') ? ZH_TW : ZH_CN;
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 获取当前语言标题
  const getCurrentLanguageTitle = (music) => {
    const lang = i18n.language || 'cn';
    const title = (() => {
      switch (lang) {
        case 'cn':
          return music.title_zh_cn || music.title;
        case 'tw':
          return music.title_zh_tw || music.title;
        case 'ja':
          return music.title_ja || music.title;
        case 'en':
          return music.title_en || music.title;
        default:
          return music.title;
      }
    })();
    return normalizeInstrumentalTitle(music, title);
  };

  // 获取当前语言描述
  const getCurrentLanguageDescription = (music) => {
    const lang = i18n.language || 'cn';
    switch (lang) {
      case 'cn':
        return music.description_zh_cn || music.description;
      case 'tw':
        return music.description_zh_tw || music.description;
      case 'ja':
        return music.description_ja || music.description;
      case 'en':
        return music.description_en || music.description;
      default:
        return music.description;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-white/70">{t('musicDetail.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !music) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || t('musicDetail.musicNotFound')}</p>
          <button
            onClick={() => spaNavigate(-1)}
            className={`${pillBtnBase} ${pillBtnPrimary}`}
          >
            {t('musicDetail.back')}
          </button>
        </div>
      </div>
    );
  }

  // 生成 SEO 信息
  const siteUrl = typeof window !== 'undefined' 
    ? `${window.location.protocol}//${window.location.host}`
    : 'https://tianliu.ai';
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : `/music/${id}`;
  const musicUrl = `${siteUrl}${currentPath}`;
  const coverUrl = getCoverImageUrl(music);
  const musicImage = coverUrl
    ? (coverUrl.startsWith('http') ? coverUrl : `${siteUrl}${coverUrl}`)
    : `${siteUrl}/favicon512.png`;
  const albumTitle = music.album || t('musicDetail.albumUnknown', '单曲');
  const musicTitle = `${getCurrentLanguageTitle(music)} - ${albumTitle} - 天流音楽`;
  const musicDescription = getCurrentLanguageDescription(music) || `聆听 ${getCurrentLanguageTitle(music)}${music.artist ? ` by ${music.artist}` : ''} - 天流音楽`;
  
  // 生成 hreflang 链接
  const hreflangLinks = [
    { lang: 'ja', url: `${siteUrl}/ja/music/${id}.html` },
    { lang: 'cn', url: `${siteUrl}/cn/music/${id}.html` },
    { lang: 'tw', url: `${siteUrl}/tw/music/${id}.html` },
    { lang: 'en', url: `${siteUrl}/en/music/${id}.html` },
    { lang: 'x-default', url: `${siteUrl}/ja/music/${id}.html` }
  ];
  
  // 准备音乐对象用于结构化数据
  const musicForSEO = music ? {
    title: getCurrentLanguageTitle(music),
    artist: music.artist || '天流音楽',
    duration: music.duration,
    cover_image: musicImage,
    description: musicDescription,
    audio_url: music.file_path ? (music.file_path.startsWith('http') ? music.file_path : `${siteUrl}${music.file_path}`) : undefined,
    album: music.album
  } : null;

  const isStaticPage = (() => {
    if (typeof window === 'undefined') return false;
    const htmlEl = document.documentElement;
    if (htmlEl && htmlEl.getAttribute('data-tenryu-static') === '1') return true;
    if (document.querySelector('meta[name="tenryu-static"][content="1"]')) return true;
    const path = window.location.pathname;
    return /\.html$/.test(path) && path !== '/index.html';
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 pt-1 pb-6 text-white">
      <SEO
        title={musicTitle}
        description={musicDescription}
        image={musicImage}
        url={musicUrl}
        type="music.song"
        music={musicForSEO}
        hreflang={hreflangLinks}
      />
      <div className="container mx-auto px-4">
        {/* 返回按钮（静态页隐藏） */}
        {!isStaticPage && (
          <button
            onClick={() => spaNavigate(-1)}
            className={`mb-2 ${pillBtnBase} ${pillBtnNeutral}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('musicDetail.back')}
          </button>
        )}

        <div className={[theme.surfaces.modalPanel, 'rounded-md shadow-2xl overflow-hidden'].join(' ')}>
          {/* 主要内容区域 */}
          <div className="md:flex">
            {/* 左侧：封面图片 */}
            <div className="md:w-1/3 p-5 bg-gradient-to-br from-blue-500/35 to-indigo-600/25">
              <div className="relative">
                {coverUrl ? (
                  <img
                    src={coverUrl}
                    alt={getCurrentLanguageTitle(music)}
                    className="w-full rounded-lg shadow-2xl"
                  />
                ) : (
                  <div className={[theme.surfaces.glassItem, 'w-full aspect-square rounded-lg flex items-center justify-center'].join(' ')}>
                    <svg className="w-24 h-24 text-white/40" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                    </svg>
                  </div>
                )}
                {music.release_date && (
                  <div className="absolute bottom-2 left-2 px-2 py-1 text-xs text-white/90 bg-black/55 backdrop-blur-sm rounded-md border border-white/15 pointer-events-none">
                    {formatDate(music.release_date)}
                  </div>
                )}
                {/* MTV 图标（如果是视频） */}
                {hasVideo(music) && (
                  <img
                    src="/icons/mtv.png"
                    alt="MTV"
                    className="absolute top-2 right-2 w-20 h-20 object-contain"
                  />
                )}
                {/* 纯音乐图标 */}
                {!hasVideo(music) && isInstrumentalMusic(music) && (
                  <img
                    src="/icons/mcd.png"
                    alt={t('musicList.categoryInstrumental', '纯音乐')}
                    title={t('musicList.categoryInstrumental', '纯音乐')}
                    className="absolute top-2 right-2 w-20 h-20 object-contain"
                  />
                )}
              </div>
            </div>

            {/* 右侧：音乐信息 */}
            <div className="md:w-2/3 p-5">
              {/* 标题和艺术家 */}
              <h1 className="text-2xl font-bold text-white mb-1 flex items-center gap-1">
                {hasVideo(music) && (
                  <img
                    src="/icons/mtv-s.png"
                    alt="MTV"
                    className="w-6 h-6 flex-shrink-0 object-contain"
                    title={t('player.hasVideo') || '视频'}
                  />
                )}
                {!hasVideo(music) && isInstrumentalMusic(music) && (
                  <img
                    src="/icons/mcd-s.png"
                    alt={t('musicList.categoryInstrumental', '纯音乐')}
                    className="w-6 h-6 flex-shrink-0 object-contain"
                    title={t('musicList.categoryInstrumental', '纯音乐')}
                  />
                )}
                <span className="min-w-0 truncate">{getCurrentLanguageTitle(music)}</span>
                {isCached && (
                  <img
                    src="/icons/regular_smile.png"
                    alt={t('musicDetail.cached')}
                    className="w-4 h-4 flex-shrink-0 cursor-pointer"
                    title={t('musicList.cachedOffline')}
                    onClick={(e) => {
                      e.stopPropagation();
                      showUiHint(t('musicList.cachedOffline'), { anchorEl: e.currentTarget });
                    }}
                  />
                )}
              </h1>
              {music.album && (
                <p className="text-base text-white/70 mb-3">{music.album}</p>
              )}

              {/* 操作按钮 */}
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  onClick={handlePlay}
                  className={`${pillBtnBase} ${pillBtnPrimary}`}
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                  </svg>
                  {t('musicDetail.play')}
                </button>
                
                <button
                  onClick={toggleFavorite}
                  className={`${pillBtnBase} ${
                    isFavorite
                      ? pillBtnDanger
                      : pillBtnNeutral
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {t('musicDetail.favorite')} ({favoritesCount})
                </button>

                <button
                  onClick={handleShare}
                  className={`${pillBtnBase} ${pillBtnNeutral}`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  {t('musicDetail.share')}
                </button>
              </div>

              {/* 下载按钮（单独一行，带说明文字） */}
              {music.allow_download && (
                <div className="flex flex-col items-start mb-3">
                  <button
                    onClick={handleDownload}
                    className={`${pillBtnBase} ${pillBtnNeutral}`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {t('musicDetail.download')}
                  </button>
                  <p className="text-xs text-white/55 mt-1">{t('musicDetail.licenseNote')}</p>
                  <button
                    onClick={() => setLicenseModalOpen(true)}
                    className="text-xs text-blue-400 hover:text-blue-300 mt-1 underline transition-colors"
                  >
                    {t('musicDetail.licenseLink')}
                  </button>
                </div>
              )}

              {/* 音乐信息 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-xs">
                <div>
                  <span className="text-white/55">{t('musicDetail.duration')}</span>
                  <p className="font-semibold text-white">{formatDuration(music.duration)}</p>
                </div>
                <div>
                  <span className="text-white/55">{t('musicDetail.playCount')}</span>
                  <p className="font-semibold text-white">{music.play_count || 0}</p>
                </div>
                {music.album && (
                  <div>
                    <span className="text-white/55">{t('musicDetail.album')}</span>
                    <p className="font-semibold text-white">{music.album}</p>
                  </div>
                )}
                {music.release_date && (
                  <div>
                    <span className="text-white/55">{t('musicDetail.releaseDate')}</span>
                    <p className="font-semibold text-white">{formatDate(music.release_date)}</p>
                  </div>
                )}
              </div>

              {/* 标签 */}
              {music.tags && music.tags.length > 0 && (
                <div className="mb-1.5">
                  <span className="text-white/55 text-xs">{t('musicDetail.tags')}</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {music.tags
                      .filter(tag => !tag.startsWith('sourceFile='))
                      .map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 bg-blue-500/15 text-blue-200 rounded-full text-xs border border-blue-400/20"
                      >
                        {translateTag(tag, t)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 描述 */}
              {getCurrentLanguageDescription(music) && (
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-white mb-1">{t('musicDetail.description')}</h3>
                  <p className="text-white/70 leading-relaxed">
                    {getCurrentLanguageDescription(music)}
                  </p>
                </div>
              )}

              {/* 歌曲说明（HTML，支持多语言翻译版） */}
              {descriptionHtmlPath && (
                <div className="mb-2">
                  <div className="bg-black/20 border border-white/10 rounded-lg overflow-hidden">
                    <iframe
                      key={descriptionHtmlPath}
                      src={descriptionHtmlPath}
                      title="song-description"
                      className="w-full min-h-[240px] bg-transparent"
                      sandbox="allow-scripts allow-same-origin"
                    />
                  </div>
                </div>
              )}

              {/* 歌词：放在歌曲说明下面（显示 txt 内容） */}
              {lyrics && (
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-semibold text-white">{t('musicDetail.lyrics')}</h3>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder={t('musicDetail.searchLyricsPlaceholder')}
                        value={lyricsSearchQuery}
                        onChange={(e) => searchLyrics(e.target.value)}
                        className={[
                          theme.inputs.base,
                          'w-44 px-2 py-0.5 text-xs rounded-full bg-black/20'
                        ].join(' ')}
                      />
                      <button
                        onClick={() => setShowFullLyrics(!showFullLyrics)}
                        className={`${pillBtnBase} ${pillBtnNeutral}`}
                      >
                        {showFullLyrics ? t('musicDetail.collapse') : t('musicDetail.expand')}
                      </button>
                    </div>
                  </div>
                  <div className={`bg-black/20 border border-white/10 rounded-lg p-3 ${showFullLyrics ? '' : 'max-h-48 overflow-y-auto'}`}>
                    <pre
                      className="whitespace-pre-wrap text-sm text-white/70 font-mono"
                      dangerouslySetInnerHTML={{ __html: highlightedLyrics || lyrics }}
                    />
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* 相关推荐 */}
        {relatedMusics.length > 0 && (
          <div className="mt-4">
            <h2 className="text-lg font-semibold text-white mb-2">{t('musicDetail.relatedRecommendations')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {relatedMusics.map((relatedMusic) => (
                <div
                  key={relatedMusic.id}
                  onClick={() => spaNavigate(`/${currentLang}/music/${relatedMusic.id}.html`)}
                  className="bg-gray-900/70 border border-white/10 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                >
                  {(getCoverImageUrl(relatedMusic) || hasVideo(relatedMusic)) ? (
                      <img
                        src={getCoverImageUrl(relatedMusic) || '/icons/mtv.png'}
                        alt={relatedMusic.title}
                        className="w-full h-32 object-contain"
                        onError={(e) => {
                          if (hasVideo(relatedMusic)) {
                            e.target.onerror = null;
                            e.target.src = '/icons/mtv.png';
                            e.target.style.display = '';
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                      />
                  ) : (
                    <div className={['w-full h-32 flex items-center justify-center', theme.surfaces.glassItem].join(' ')}>
                      <svg className="w-12 h-12 text-white/45" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                      </svg>
                    </div>
                  )}
                  <div className="p-3">
                    <h3 className="font-semibold text-sm text-white truncate">
                      {getCurrentLanguageTitle(relatedMusic)}
                    </h3>
                    {relatedMusic.album && (
                      <p className="text-xs text-white/70 truncate mt-1">
                        {relatedMusic.album}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 下载对话框 */}
      <DownloadDialog
        music={music}
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        onConfirm={handleDownloadConfirm}
      />

      {/* 音乐使用授权说明弹窗 */}
      <MusicLicenseModal
        open={licenseModalOpen}
        onClose={() => setLicenseModalOpen(false)}
      />

      <ShareDialog
        open={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          setShareDialogMeta(null);
        }}
        meta={shareDialogMeta}
        onCopy={handleShareCopy}
      />
    </div>
  );
};

export default MusicDetail;

